import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Target,
  Heart,
  TrendingUp,
  Globe,
  CheckCircle,
  Users,
  Award,
} from "lucide-react";
import { SEO } from "@/components/SEO";

export default function AboutPage() {
  return (
    <div className="font-['Plus_Jakarta_Sans']" data-testid="about-page">
      <SEO
        title="About Us"
        description="Learn about Momentum VA — an Australia-focused virtual assistant company with a dedicated team in the Philippines, supporting real estate offices with reliable, managed VA services."
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative bg-[#0B1C3E] py-20 lg:py-28" data-testid="about-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#2A9D8F] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#5D6D35] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6 font-['Outfit']">
              Dedicated Support for Australian Real Estate
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Momentum VA was created with a simple mission: to provide Australian real estate 
              professionals with reliable, process-driven virtual assistant support they can 
              truly depend on.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28 bg-white" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_momentum-admin/artifacts/0lpz48oz_DSC05611.jpg"
                  alt="Momentum VA team working together"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
            
            <div className="order-1 lg:order-2 space-y-6">
              <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">
                Why Momentum VA Exists
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                We understand the challenges Australian real estate offices face. Growing 
                workloads, staff turnover, and the constant pressure to do more with less. 
                That's why we built Momentum VA differently.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                Unlike freelancer platforms or traditional outsourcing, we focus on building 
                dedicated teams who understand your business. Our virtual assistants are 
                trained specifically for Australian real estate operations, with proper 
                systems, processes, and quality control in place.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                The result? A reliable support partner that grows with you, handles the admin 
                burden, and lets you focus on what you do best—serving your clients and 
                growing your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Australia-Philippines Model */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="delivery-model-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-[#2A9D8F]/10 rounded-full">
                <Globe className="w-5 h-5 text-[#2A9D8F] mr-2" />
                <span className="text-[#2A9D8F] font-semibold text-sm">Our Delivery Model</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">
                Australia-Philippines Partnership
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                Our team is based in the Philippines, a country renowned for its exceptional 
                English proficiency, strong work ethic, and cultural alignment with Australia. 
                This model allows us to provide professional, cost-effective support while 
                maintaining the quality Australian businesses expect.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                We invest heavily in training, systems, and quality control. Every team member 
                is trained specifically for Australian real estate operations and is managed 
                by our local team leaders who understand both cultures.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">Dedicated support aligned with Australian business hours</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">No recruitment or HR headaches for you</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">Trained professionals who understand real estate</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">Ongoing quality control and management</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
                <h3 className="text-xl font-semibold text-[#0B1C3E] mb-6 font-['Outfit']">
                  What You Get
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#5D6D35]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#5D6D35]" />
                    </div>
                    <span className="text-slate-700">Dedicated support aligned with Australian business hours</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#5D6D35]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#5D6D35]" />
                    </div>
                    <span className="text-slate-700">No recruitment or HR headaches for you</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#5D6D35]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#5D6D35]" />
                    </div>
                    <span className="text-slate-700">Trained professionals who understand real estate</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#5D6D35]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#5D6D35]" />
                    </div>
                    <span className="text-slate-700">Ongoing quality control and management</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#5D6D35]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#5D6D35]" />
                    </div>
                    <span className="text-slate-700">Scalable support as your business grows</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#5D6D35]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#5D6D35]" />
                    </div>
                    <span className="text-slate-700">Cost-effective without compromising quality</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-28 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              What Drives Us Every Day
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Our values aren't just words on a wall. They guide every interaction, 
              every process, and every relationship we build.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="value-card-0">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Trust</h3>
              <p className="text-slate-600">Building lasting relationships through transparency and reliability.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="value-card-1">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Consistency</h3>
              <p className="text-slate-600">Delivering dependable support day after day, without exception.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="value-card-2">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Growth</h3>
              <p className="text-slate-600">Helping your business scale while developing our team's potential.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="value-card-3">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Partnership</h3>
              <p className="text-slate-600">Working alongside you as an extension of your team, not just a vendor.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Approach */}
      <section className="py-20 md:py-28 bg-[#0B1C3E]" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
                Our Approach
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit']">
                We Manage. You Grow.
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                When you partner with Momentum VA, you're not just getting a virtual 
                assistant—you're getting a fully managed support solution. We handle 
                recruitment, training, performance management, and quality control.
              </p>
              <p className="text-slate-300 text-lg leading-relaxed">
                Your role? Simply tell us what you need, and we'll make it happen. 
                It's support without the stress of managing another team member.
              </p>
              
              <div className="flex items-center space-x-4 pt-4">
                <div className="flex -space-x-3">
                  <div className="w-12 h-12 rounded-full bg-[#2A9D8F] border-2 border-[#0B1C3E] flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#2A9D8F] border-2 border-[#0B1C3E] flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#2A9D8F] border-2 border-[#0B1C3E] flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <p className="text-white font-semibold">100+ Businesses</p>
                  <p className="text-slate-400 text-sm">Trust Momentum VA</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-6 font-['Outfit']">
                The Momentum Difference
              </h3>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-200">Real estate-trained professionals</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-200">Australian business hour availability</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-200">Dedicated team leaders</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-200">Regular performance reporting</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-200">Continuous process improvement</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-200">Long-term partnership focus</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50" data-testid="about-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mb-6 font-['Outfit']">
            Ready to Learn More?
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how Momentum VA can support your real estate office. 
            Book a free discovery call—no obligation, just a conversation.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg"
              data-testid="about-cta-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Book Your Discovery Call
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
